import { action, computed } from '@ember/object';
import { isBlank, isNone } from '@ember/utils';
import { equalityOperators, existenceOperators } from 'cio/utils/operators';
import AttributeCondition from './attribute';

export enum SpecialDeviceAttributes {
  id = 'id',
  platform = 'platform',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _last_status = '_last_status',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _last_used = '_last_used',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  device_model = 'device_model',
}

export const isSpecialDeviceAttribute = (attr: string) => {
  if (isBlank(attr)) {
    return false;
  }
  let key = attr as keyof typeof SpecialDeviceAttributes;
  return !isNone(SpecialDeviceAttributes[key]);
};

export const isDeviceExistsCondition = (attr: string) => {
  if (isBlank(attr)) {
    return false;
  }
  return attr === SpecialDeviceAttributes.id;
};

const DEVICE_MODEL_OPERATORS = [...equalityOperators, ...existenceOperators];
const DEFAULT_EXISTENCE_OPERATOR = existenceOperators[0].value;
const DEFAULT_EQUALITY_OPERATOR = equalityOperators[0].value;
const DEFAULT_PLATFORM = 'ios';
const LAST_STATUS_OPERATORS = [
  { label: 'is', value: 'eq' },
  { label: 'is not', value: '!eq' },
];
const LAST_USED_OPERATORS = [
  {
    label: 'is after',
    value: 'timestamp_gt',
  },
  {
    label: 'is before',
    value: 'timestamp_lt',
  },
  {
    label: 'is between',
    value: 'timestamp_bw',
  },
  {
    label: 'has not been used',
    value: '!exists',
  },
];

type OptionItem = { label: string; value: any };

const SPECIAL_DEVICE_ATTRIBUTES_CONFIG: Record<
  keyof typeof SpecialDeviceAttributes,
  {
    defaultOperator: string;
    defaultValue?: any;
    label: string;
    operators: OptionItem[];
    values: OptionItem[];
  }
> = {
  id: {
    defaultOperator: DEFAULT_EXISTENCE_OPERATOR,
    label: 'exists',
    operators: existenceOperators,
    values: [
      { value: 'true', label: 'true' },
      { value: 'false', label: 'false' },
    ],
  },
  platform: {
    defaultOperator: DEFAULT_EQUALITY_OPERATOR,
    defaultValue: DEFAULT_PLATFORM,
    label: SpecialDeviceAttributes.platform,
    operators: equalityOperators,
    values: [
      { value: 'ios', label: 'iOS' },
      { value: 'android', label: 'Android' },
    ],
  },
  _last_status: {
    defaultOperator: LAST_STATUS_OPERATORS[0].value,
    defaultValue: 'bounced',
    label: 'last status',
    operators: LAST_STATUS_OPERATORS,
    values: [
      { value: 'bounced', label: 'bounced' },
      { value: 'suppressed', label: 'suppressed' },
      { value: 'sent', label: 'sent' },
      { value: 'unused', label: 'unused' },
    ],
  },
  _last_used: {
    defaultOperator: LAST_USED_OPERATORS[0].value,
    label: 'last used',
    operators: LAST_USED_OPERATORS,
    values: [],
  },
  device_model: {
    defaultOperator: DEFAULT_EQUALITY_OPERATOR,
    label: 'device model',
    operators: DEVICE_MODEL_OPERATORS,
    values: [],
  },
};

export const getSpecialDeviceAttributeConfig = (attr: string) => {
  if (isBlank(attr)) {
    return null;
  }
  let specialAttribute = attr as keyof typeof SpecialDeviceAttributes;
  return SPECIAL_DEVICE_ATTRIBUTES_CONFIG[specialAttribute];
};

class DeviceAttributeCondition extends AttributeCondition {
  type = 'device_attribute';

  constructor() {
    super();
    this.operator = DEFAULT_EQUALITY_OPERATOR;
  }

  @computed('name')
  get isSpecialAttribute() {
    return isSpecialDeviceAttribute(this.name);
  }

  @computed('name')
  get isDeviceExistsCondition() {
    return isDeviceExistsCondition(this.name);
  }

  get disallowDateOperators() {
    return false;
  }

  @action
  duplicate() {
    let duplicate = DeviceAttributeCondition.create();
    this.copyAllAttributes(this, duplicate);
    return duplicate;
  }

  toString() {
    return 'DeviceAttributeCondition';
  }
}

export default DeviceAttributeCondition;
